import React, { useEffect, useState } from "react";
import api, { apiError, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, DateField } from "@/components/fields";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Plus, FilePdf, EnvelopeSimple, Trash, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CHARGE_TYPES = ["Rent", "Water", "Electricity", "Internet", "Service Charge", "Repairs", "Penalties", "Other Charges"];

export default function Invoices() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [leaseId, setLeaseId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [applyVat, setApplyVat] = useState(false);
  const [lines, setLines] = useState([{ description: "Rent", amount: 0 }]);

  const canCreate = ["property_manager", "property_admin", "accountant"].includes(user.role);

  const load = () => {
    api.get("/invoices").then((r) => setItems(r.data));
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    api.get("/leases").then((r) => setLeases(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setLines([...lines, { description: "Other Charges", amount: 0 }]);
  const setLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: k === "amount" ? Number(v) : v } : l));
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const subtotal = lines.reduce((s, l) => s + Number(l.amount || 0), 0);

  const create = async () => {
    if (!tenantId) return toast.error("Select a tenant");
    if (subtotal <= 0) return toast.error("Add at least one charge");
    try {
      const lease = leases.find((l) => l.id === leaseId);
      await api.post("/invoices", {
        tenant_id: tenantId, lease_id: leaseId || null, property_id: lease?.property_id || null,
        items: lines, due_date: dueDate || null, apply_vat: applyVat,
      });
      toast.success("Invoice created"); setOpen(false);
      setLines([{ description: "Rent", amount: 0 }]); setTenantId(""); setLeaseId(""); setApplyVat(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const email = async (id) => {
    try { const r = await api.post(`/invoices/${id}/email`); 
      if (r.data.status === "success") toast.success("Invoice emailed to tenant");
      else toast.error("Email failed: " + (r.data.detail || r.data.reason || "unknown"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const notify = async (id) => {
    try { const r = await api.post(`/invoices/${id}/notify`);
      if (!r.data.configured) toast.message("Twilio not configured — SMS/WhatsApp skipped");
      else toast.success("SMS & WhatsApp reminder sent");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;
  const tenantLeases = leases.filter((l) => l.tenant_id === tenantId);

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Tenant billing & charges" icon={FileText}
        actions={canCreate && <Button onClick={() => setOpen(true)} data-testid="add-invoice-btn" className="gap-2"><Plus size={16} /> New Invoice</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices yet" message="Create an invoice to bill a tenant."
          action={canCreate && <Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />New Invoice</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Invoice #</TableHead><TableHead>Tenant</TableHead><TableHead>Due</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-mono text-sm">{inv.invoice_number}<div className="text-xs text-muted-foreground">{inv.billing_code}</div></TableCell>
                    <TableCell className="text-sm">{inv.tenant_name}<div className="text-xs text-muted-foreground">{inv.property_name} {inv.unit_no}</div></TableCell>
                    <TableCell className="text-sm">{fmtDate(inv.due_date)}</TableCell>
                    <TableCell className="text-right font-mono">{money(inv.total, cur)}</TableCell>
                    <TableCell className="text-right font-mono">{money(inv.amount_paid, cur)}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" title="PDF" onClick={() => openPdf(`/invoices/${inv.id}/pdf`)} data-testid={`inv-pdf-${inv.id}`}><FilePdf size={16} /></Button>
                        <Button size="icon" variant="ghost" title="Email" onClick={() => email(inv.id)} data-testid={`inv-email-${inv.id}`}><EnvelopeSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" title="SMS & WhatsApp" onClick={() => notify(inv.id)} data-testid={`inv-notify-${inv.id}`}><ChatCircle size={16} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectField label="Tenant" value={tenantId} onChange={(v) => { setTenantId(v); setLeaseId(""); }}
                options={tenants.map((t) => ({ value: t.id, label: t.full_name }))} required testId="invoice-tenant" />
              <SelectField label="Lease (optional)" value={leaseId} onChange={setLeaseId}
                options={[{ value: "", label: "— None —" }, ...tenantLeases.map((l) => ({ value: l.id, label: `${l.property_name} ${l.unit_no}` }))]} />
              <DateField label="Due Date" value={dueDate} onChange={setDueDate} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Charges</span>
                <Button size="sm" variant="outline" onClick={addLine} className="gap-1" data-testid="add-line-btn"><Plus size={14} /> Add Charge</Button>
              </div>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1"><SelectField value={l.description} onChange={(v) => setLine(i, "description", v)} options={CHARGE_TYPES} /></div>
                    <div className="w-36"><TextField type="number" value={l.amount} onChange={(v) => setLine(i, "amount", v)} testId={`line-amount-${i}`} /></div>
                    {lines.length > 1 && <Button size="icon" variant="ghost" onClick={() => rmLine(i)}><Trash size={16} className="text-destructive" /></Button>}
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={applyVat} onCheckedChange={(v) => setApplyVat(!!v)} /> Apply VAT</label>
            <div className="flex justify-end text-sm font-medium">Subtotal: <span className="font-mono ml-2">{money(subtotal, cur)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-invoice-btn">Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
